import Dialog from "@mui/material/Dialog"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faXmark } from "@fortawesome/pro-solid-svg-icons"

/**
 * Large view for a single uploaded image.
 *
 * Deliberately self-contained: the site-visit lane has its own `ImageViewer`,
 * but that one is typed to `VisitPhoto` and pulls in `siteVisit.module.scss`,
 * so reusing it here would drag page-level styles into a shared component.
 *
 * Rendered only when `url` is set, so the caller can keep one piece of state
 * for "which image is open" rather than a separate open flag.
 */
type ImagePreviewProps = {
    url?: string
    /** Shown in the header strip; falls back to a generic label. */
    name?: string
    onClose: () => void
}

const ImagePreview = ({ url, name, onClose }: ImagePreviewProps) =>
    url ? (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            onClose={onClose}
            PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden", margin: "16px" } }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    padding: "10px 12px",
                    background: "#121726",
                    color: "#fff",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "12.5px",
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {name || "Image"}
                </Typography>
                <button
                    type="button"
                    aria-label="Close image"
                    onClick={onClose}
                    style={{
                        display: "grid",
                        placeItems: "center",
                        width: "26px",
                        height: "26px",
                        flex: "0 0 26px",
                        border: "none",
                        borderRadius: "50%",
                        background: "rgba(255, 255, 255, 0.14)",
                        color: "#fff",
                        cursor: "pointer",
                        padding: 0,
                    }}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </Box>

            {/* Contained, not cropped — the surveyor is checking the photo is
                legible, so the whole frame has to be visible. */}
            <Box sx={{ background: "#0d1320", display: "grid", placeItems: "center" }}>
                <img
                    src={url}
                    alt={name || ""}
                    style={{
                        maxWidth: "100%",
                        maxHeight: "72vh",
                        objectFit: "contain",
                        display: "block",
                    }}
                />
            </Box>
        </Dialog>
    ) : null

export default ImagePreview
